import { useParams } from "react-router";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { WorkOrderPdf } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/work-order-pdf";
import { useGetWorkOrder } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-work-order-offline";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentImmutableAlert } from "@/shared/bsm/core/document/components/ui/document-immutable-alert";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import { WorkOrderProviders } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-providers";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/htz/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { FulfillmentStateBadge } from "@/services/backend/relationships/components/fulfillment-state-badge";
import { Separator } from "@/shared/components/ui/separator";
import { SetWorkOrderImmutableDialog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/set-work-order-immutable-dialog";
import { ConnectInvoiceExportDialogHtz } from "@/services/backend/connect/components/connect-invoice-export-htz";
import { CancelWorkOrderDialog } from "@/services/backend/htz/work-order/components/cancel-work-order-dialog";
import { CompleteWorkOrderDialog } from "@/services/backend/htz/work-order/components/complete-work-order-dialog";
import { BackToPlanningDialog } from "@/services/backend/htz/work-order/components/back-to-planning-dialog";
import { WorkOrder } from "@/services/backend/htz/work-order/types/work-order";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-breadcrumbs";

export function WorkOrderCompletionRoute() {
  const { workOrderId } = useParams();

  return (
    <WorkOrderProviders workOrderId={workOrderId!}>
      <WorkOrderCompletionPage workOrderId={workOrderId!} />
    </WorkOrderProviders>
  );
}

function WorkOrderCompletionPage({ workOrderId }: { workOrderId: string }) {
  const { data: workOrder, isLoading, error } = useGetWorkOrder(workOrderId);

  useWorkOrderStateGuard(
    workOrder,
    FulfillmentState.Executed,
    FulfillmentState.PartiallyExecuted,
    FulfillmentState.Completed,
    FulfillmentState.Cancelled,
  );

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="completion">
      {t("Nachbereitung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error ? <RTKQueryErrorAlert error={error} /> : null}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && <Page workOrder={workOrder} />}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  const { notReadyError } = useErrDocumentNotReady();

  const hasDataError = hasFieldError(
    notReadyError,
    "state",
    "executingBranchId",
    "beginDate",
    "endDate",
    "teamLeader",
    "address",
    "representative",
  );
  const hasPosError = hasFieldError(notReadyError, "position");

  const links = [
    { to: "data", hasValidationError: hasDataError, label: t("Arbeitsschein") },
    {
      to: "positions",
      hasValidationError: hasPosError,
      label: t("Leistungen"),
    },
    {
      to: "laboratory",
      label: t("Labor"),
    },
  ];

  return (
    <DocumentPage>
      <DocumentImmutableAlert document={workOrder} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              hasValidationError={link.hasValidationError}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <FulfillmentStateBadge state={workOrder.state} />
        <DocumentPageActions>
          {!workOrder.immutable && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <Separator orientation="vertical" className="min-h-8" />
              <SetWorkOrderImmutableDialog workOrder={workOrder} />
              <WorkOrderPdf workOrder={workOrder} />
              <ConnectInvoiceExportDialogHtz workOrder={workOrder} />
              <Separator orientation="vertical" className="min-h-8" />
              {[
                FulfillmentState.Completed,
                FulfillmentState.Cancelled,
              ].includes(workOrder.state) ? (
                <BackToPlanningDialog workOrder={workOrder} />
              ) : (
                <>
                  <CancelWorkOrderDialog workOrder={workOrder} />
                  <CompleteWorkOrderDialog workOrder={workOrder} />
                </>
              )}
            </>
          )}
          {workOrder.immutable && <WorkOrderPdf workOrder={workOrder} />}
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
